// src/components/Footer/Footer.tsx
import React from 'react'
import { Link } from 'react-router-dom'
import { MdPerson, MdEmail, MdPhone, MdLink } from 'react-icons/md'
import { FaTelegram } from 'react-icons/fa'
import styles from './Footer.module.scss'
import {
  PERSONAL_DATA_AGREEMENT_ROUTE,
  PRIVACY_POLICY_ROUTE,
  USER_AGREEMENT_ROUTE,
} from '../../utils/constants'
import nochevkin from '../../../assets/images/team/nochevkin.webp'
import bisovka from '../../../assets/images/team/bisovka.webp'
import appStoreDark from '../../../assets/images/app-stores/app_store_dark.svg'
import appStoreLight from '../../../assets/images/app-stores/app_store_light.svg'
import ruStoreDark from '../../../assets/images/app-stores/ru_store_dark.svg'
import ruStoreLight from '../../../assets/images/app-stores/ru_store_light.svg'
import { ApkButton } from '../apkButton/ApkButton'
import { useTheme } from '../../hooks/useTheme'

// ===== Расчёт стажа с округлением вверх =====
const START_DATE = '2019-01-01'

const getExperienceYears = (startDate: string): number => {
  const start = new Date(startDate)
  const now = new Date()
  const diffMs = now.getTime() - start.getTime()
  const years = diffMs / (1000 * 60 * 60 * 24 * 365.25)
  return Math.ceil(years)
}

export const Footer: React.FC = () => {
  const ruStoreUrl = process.env.REACT_APP_RUSTORE_URL
  const directDownloadUrl = process.env.REACT_APP_DIRECT_DOWNLOAD_URL
  const appStoreUrl = process.env.REACT_APP_APPSTORE_URL
  const COUNTRY = process.env.REACT_APP_COUNTRY || 'ru'
  const DOMAIN = COUNTRY === 'ru' ? 'ru' : COUNTRY
  const { isDark } = useTheme()

  const appStoreBadge = isDark ? appStoreLight : appStoreDark
  const ruStoreBadge = isDark ? ruStoreLight : ruStoreDark

  // ===== Стаж в IT =====
  const experienceYears = getExperienceYears(START_DATE)

  // ===== Команда (внутри — чтобы видеть experienceYears) =====
  const teamMembers = [
    {
      id: 1,
      name: 'Сергей Ночевкин',
      role: 'основатель',
      bio: `17 лет в автобизнесе и логистике, ${experienceYears} лет в IT, команды до 300 человек, стартапы`,
      email: 'sergey.nochevkin@seacoding.ru',
      phone: '+7 (911) 833-82-21',
      telegram: 'https://t.me/sergey_nochevkin',
      photo: nochevkin,
      link: 'https://nochevkin.ru',
    }
  ]

  return (
    <footer className={styles.footer}>
      <div className={styles.container}>
        <div className={styles.grid}>
          {/* Колонка 1 */}
          <div className={styles.column}>
            <h2 className={styles.logo}>ЛОГАЙДИ</h2>
            <p className={styles.description}>Операционная система для бизнеса</p>
            <div className={styles.contacts}>
              <a href={`mailto:support@logid.${DOMAIN}`} className={styles.link}>
                support@logid.{DOMAIN}
              </a>
              <a href="tel:+79940822881" className={styles.link}>
                +7 (994) 082-28-81
              </a>
            </div>
          </div>

          {/* Колонка 2: Команда */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Команда</h3>
            <div className={styles.teamList}>
              {teamMembers.map((member) => (
                <div key={member.id} className={styles.teamMember}>
                  <div className={styles.avatar}>
                    {member.photo ? (
                      <img src={member.photo} alt={member.name} className={styles.avatarImage} />
                    ) : (
                      <MdPerson size={48} />
                    )}
                  </div>
                  <div className={styles.teamInfo}>
                    <div className={styles.teamName}>{member.name}</div>
                    <div className={styles.teamRole}>{member.role}</div>
                    {member.bio && <div className={styles.teamBio}>{member.bio}</div>}

                    <div className={styles.teamContacts}>
                      {member.email && (
                        <a href={`mailto:${member.email}`} className={styles.teamContactLink}>
                          <MdEmail size={14} />
                          <span>{member.email}</span>
                        </a>
                      )}
                      {member.phone && (
                        <a
                          href={`tel:${member.phone.replace(/\s/g, '')}`}
                          className={styles.teamContactLink}
                        >
                          <MdPhone size={14} />
                          <span>{member.phone}</span>
                        </a>
                      )}
                      {member.telegram && (
                        <a
                          href={member.telegram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.teamContactLink}
                        >
                          <FaTelegram size={14} />
                          <span>Telegram</span>
                        </a>
                      )}
                      {member.link && (
                        <a
                          href={member.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.teamContactLink}
                        >
                          <MdLink size={14} />
                          <span>личный сайт</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Колонка 3: Приложения */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Скачать приложение</h3>
            <ul className={styles.apps}>
              {appStoreUrl && (
                <li>
                  <a
                    href={appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.storeLink}
                  >
                    <img
                      src={appStoreBadge}
                      alt="Скачать в App Store"
                      className={styles.storeBadge}
                    />
                  </a>
                </li>
              )}
              {ruStoreUrl && (
                <li>
                  <a
                    href={ruStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.storeLink}
                  >
                    <img src={ruStoreBadge} alt="Скачать в RuStore" className={styles.storeBadge} />
                  </a>
                </li>
              )}
              {directDownloadUrl && (
                <li>
                  <a
                    href={directDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    <ApkButton downloadUrl={directDownloadUrl} />
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Колонка 4: Документы */}
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Документы</h3>
            <ul className={styles.links}>
              <li>
                <Link to={PRIVACY_POLICY_ROUTE} className={styles.link}>
                  Политика конфиденциальности
                </Link>
              </li>
              <li>
                <Link to={USER_AGREEMENT_ROUTE} className={styles.link}>
                  Пользовательское соглашение
                </Link>
              </li>
              <li>
                <Link to={PERSONAL_DATA_AGREEMENT_ROUTE} className={styles.link}>
                  Согласие на обработку данных
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.bottom}>
          <p className={styles.copyright}>© ЛОГАЙДИ {new Date().getFullYear()}</p>
          <div className={styles.studioLink}>
            Сделано в{' '}
            <a href="https://seacoding.ru" target="_blank" rel="noopener noreferrer">
              СИ КОДИНГ | SEA CODING
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
